import React, { useEffect, useState, useRef } from 'react';
import Lottie from 'lottie-react';

const HomeHero = ({ translations = {} }) => {
  const [parrotData, setParrotData] = useState(null);
  const [parrotState, setParrotState] = useState('flying'); // 'flying' | 'falling' | 'hidden'
  const parrotRef = useRef(null);
  const animationInterval = useRef(null);
  const lottieRef = useRef(null);
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [showCountdown, setShowCountdown] = useState(true);
  const countdownRef = useRef(null);
  const touchStartX = useRef(null);
  const touchCurrentX = useRef(null);

  // Load parrot animation
  useEffect(() => {
    fetch('/images/Parrot.json')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load Parrot.json');
        return res.json();
      })
      .then((data) => setParrotData(data))
      .catch((err) => console.error('Error loading Parrot.json:', err));
  }, []);

  // Handle parrot states and animations
  useEffect(() => {
    if (!parrotData) return;

    const startFlying = () => {
      if (parrotRef.current) {
        parrotRef.current.style.animation = 'none';
        parrotRef.current.style.position = 'absolute';
        parrotRef.current.style.transition = 'none';
        parrotRef.current.style.top = '280px';
        parrotRef.current.style.left = '0px';
        parrotRef.current.style.transform = 'translateX(-150px) rotateY(180deg)';
        
        parrotRef.current.offsetHeight;
        parrotRef.current.style.animation = 'fly-parrot 3s linear forwards';
      }

      if (lottieRef.current) {
        lottieRef.current.play();
      }
    };

    const scheduleNextFlight = () => {
      animationInterval.current = setTimeout(() => {
        startFlying();
        scheduleNextFlight();
      }, 8000);
    };

    startFlying();
    scheduleNextFlight();

    return () => {
      if (animationInterval.current) {
        clearTimeout(animationInterval.current);
      }
      if (countdownRef.current) {
        countdownRef.current.style.transform = 'translateX(0)';
        countdownRef.current.style.opacity = '1';
      }
    };
  }, [parrotData]);

  // Countdown timer logic
  useEffect(() => {
    const targetDate = new Date('2025-10-03T00:00:00').getTime();

    const updateCountdown = () => {
      const now = new Date().getTime();
      const distance = targetDate - now;

      if (distance <= 0) {
        setShowCountdown(false);
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, []);

  // Swipe-to-dismiss logic
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchCurrentX.current = touchStartX.current;
    countdownRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e) => {
    touchCurrentX.current = e.touches[0].clientX;
    const deltaX = touchCurrentX.current - touchStartX.current;
    countdownRef.current.style.transform = `translateX(${deltaX}px)`;
    countdownRef.current.style.opacity = `${Math.max(0.2, 1 - Math.abs(deltaX) / 200)}`;
  };

  const handleTouchEnd = () => {
    const deltaX = touchCurrentX.current - touchStartX.current;
    if (Math.abs(deltaX) > 100) {
      countdownRef.current.style.transition = 'transform 0.4s ease-out, opacity 0.4s ease-out';
      countdownRef.current.style.transform = `translateX(${deltaX > 0 ? '100vw' : '-100vw'})`;
      countdownRef.current.style.opacity = '0';
      setTimeout(() => setShowCountdown(false), 400);
    } else {
      countdownRef.current.style.transition = 'transform 0.4s ease-out, opacity 0.4s ease-out';
      countdownRef.current.style.transform = 'translateX(0)';
      countdownRef.current.style.opacity = '1';
    }
  };

  const handleParrotClick = () => {
    if (parrotState !== 'flying') return;
    
    setParrotState('falling');
    
    if (animationInterval.current) {
      clearTimeout(animationInterval.current);
      animationInterval.current = null;
    }

    if (lottieRef.current) {
      lottieRef.current.pause();
    }
    
    const audio = new Audio('/sounds/parrot.mp3');
    audio.play().catch((err) => console.error('Error playing sound:', err));

    if (parrotRef.current) {
      const rect = parrotRef.current.getBoundingClientRect();
      const currentX = rect.left;
      const currentY = rect.top;
      
      parrotRef.current.style.animation = 'none';
      parrotRef.current.style.position = 'fixed';
      parrotRef.current.style.left = `${currentX}px`;
      parrotRef.current.style.top = `${currentY}px`;
      parrotRef.current.style.transform = 'rotateY(180deg)';
      parrotRef.current.style.transition = 'transform 3s ease-in, top 3s ease-in';
      
      setTimeout(() => {
        if (parrotRef.current) {
          parrotRef.current.style.top = `${window.innerHeight + 200}px`;
        }
      }, 50);
      
      setTimeout(() => {
        setParrotState('hidden');
        if (parrotRef.current) {
          parrotRef.current.style.position = 'absolute';
          parrotRef.current.style.left = '0px';
          parrotRef.current.style.top = '280px';
        }
      }, 3000);
    }
  };

  return (
    <section className="relative h-screen flex items-center justify-center bg-white overflow-hidden">
      {/* Parrot */}
      {parrotData && parrotState !== 'hidden' && (
        <div
          ref={parrotRef}
          className="absolute w-32 z-20 cursor-pointer"
          style={{
            top: '280px',
            left: '0px',
            filter: 'drop-shadow(0 10px 10px rgba(0,0,0,0.2))',
            transform: parrotState === 'flying' ? 'translateX(-150px) rotateY(180deg)' : parrotState === 'falling' ? 'rotateY(180deg) rotate(270deg)' : '',
            animation: parrotState === 'flying' ? 'fly-parrot 3s linear forwards' : 'none'
          }}
          onClick={handleParrotClick}
          onTouchStart={handleParrotClick}
        >
          <Lottie 
            animationData={parrotData} 
            loop={true} 
            autoplay={true}
            lottieRef={lottieRef}
          />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 text-center text-[#1F2937] p-6 max-w-4xl animate-fade-in-up">
        <img
          src="/images/logo.png"
          alt="El Latino Cafe Logo"
          className="mx-auto mb-6 max-h-[400px] w-auto object-contain animate-logo-zoom-marked"
        />
        <h1 className="text-3xl md:text-5xl font-semibold leading-snug tracking-tight text-gray-900 mb-2">
          {translations.heroTitle || {
            en: 'Experience the Soul of Latin America',
            es: 'Experimenta el Alma de América Latina',
            gr: 'Ζήστε την Ψυχή της Λατινικής Αμερικής'
          }[translations.language || 'en']}
        </h1>
        <p className="mt-4 text-xl md:text-2xl font-light leading-relaxed tracking-wide text-gray-700">
          {translations.heroSubtitle || {
            en: 'Authentic flavors, vibrant music, and an unforgettable atmosphere.',
            es: 'Sabores auténticos, música vibrante y una atmósfera inolvidable.',
            gr: 'Αυθεντικές γεύσεις, ζωντανή μουσική και μια αξέχαστη ατμόσφαιρα.'
          }[translations.language || 'en']}
        </p>
        {showCountdown && (
          <div
            ref={countdownRef}
            className="mt-4 animate-fade-in-up animate-shake"
            style={{ transition: 'transform 0.4s ease-out, opacity 0.4s ease-out' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <p className="text-lg md:text-xl font-semibold text-[#FF4C29] mb-1">
              {translations.countdownText || {
                en: 'Grand Opening 3 October',
                es: 'Gran Apertura 3 de Octubre',
                gr: 'Μεγάλο Άνοιγμα 3 Οκτωβρίου'
              }[translations.language || 'en']}
            </p>
            <div className="grid grid-cols-4 gap-1 max-w-[260px] mx-auto bg-white/80 p-1 rounded-md border border-gray-200">
              <div className="flex flex-col items-center">
                <span className="text-xl md:text-2xl font-bold text-[#FF4C29]">{timeLeft.days}</span>
                <span className="text-xs md:text-sm text-gray-700">
                  {translations.daysLabel || {
                    en: 'Days',
                    es: 'Días',
                    gr: 'Ημέρες'
                  }[translations.language || 'en']}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-xl md:text-2xl font-bold text-[#FF4C29]">{timeLeft.hours}</span>
                <span className="text-xs md:text-sm text-gray-700">
                  {translations.hoursLabel || {
                    en: 'Hours',
                    es: 'Horas',
                    gr: 'Ώρες'
                  }[translations.language || 'en']}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-xl md:text-2xl font-bold text-[#FF4C29]">{timeLeft.minutes}</span>
                <span className="text-xs md:text-sm text-gray-700">
                  {translations.minutesLabel || {
                    en: 'Minutes',
                    es: 'Minutos',
                    gr: 'Λεπτά'
                  }[translations.language || 'en']}
                </span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-xl md:text-2xl font-bold text-[#FF4C29]">{timeLeft.seconds}</span>
                <span className="text-xs md:text-sm text-gray-700">
                  {translations.secondsLabel || {
                    en: 'Seconds',
                    es: 'Segundos',
                    gr: 'Δευτερόλεπτα'
                  }[translations.language || 'en']}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fly-parrot {
          0% {
            transform: translateX(-150px) translateY(0) rotateY(180deg);
          }
          25% {
            transform: translateX(25vw) translateY(-20px) rotateY(180deg);
          }
          50% {
            transform: translateX(50vw) translateY(0) rotateY(180deg);
          }
          75% {
            transform: translateX(75vw) translateY(20px) rotateY(180deg);
          }
          100% {
            transform: translateX(100vw) translateY(0) rotateY(180deg);
          }
        }

        .animate-logo-zoom-marked {
          animation: logo-zoom-marked 1.2s ease-out forwards;
        }

        @keyframes logo-zoom-marked {
          0% { transform: scale(0.1); opacity: 0; }
          50% { transform: scale(1.2); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }

        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }

        @keyframes fade-in-up {
          0% { transform: translateY(20px); opacity: 0; }
          100% { transform: translateY(0); opacity: 1; }
        }

        @keyframes shake {
          0% { transform: translateX(0); }
          25% { transform: translateX(5px); }
          50% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
          100% { transform: translateX(0); }
        }

        .animate-shake {
          animation: shake 1.5s ease-in-out 2;
        }
      `}</style>
    </section>
  );
};

export default HomeHero;